// src/hooks/useSpellgatherAvg.ts
import { useEffect, useRef, useState } from "react";

/** tiny in-memory cache */
const MEMO: Record<string, { t: number; data: { ok: boolean; avg: number | null } }> = {};
const TTL = 60_000; // 60s

function getEnv(key: string): string | undefined {
  // Vite-first, but also tolerate process.env during SSR/build pipelines
  const viteVal = (import.meta as any)?.env?.[key];
  if (viteVal) return viteVal as string;
  // @ts-ignore
  if (typeof process !== "undefined" && process?.env) return (process as any).env[key];
  return undefined;
}

/** Build a safe URL from a base (absolute or relative) and params */
function buildUrl(base: string, params: Record<string, string>): string {
  try {
    // Absolute (https:// or http://)
    const u = new URL(base);
    Object.entries(params).forEach(([k, v]) => u.searchParams.set(k, v));
    return u.toString();
  } catch {
    // Relative → use current origin
    const u = new URL(base.replace(/^\/*/, "/"), window.location.origin);
    Object.entries(params).forEach(([k, v]) => u.searchParams.set(k, v));
    return u.toString();
  }
}

/**
 * Optional Spellgather average price hook.
 * If VITE_SG_AVG_API (or NEXT_PUBLIC_SG_AVG_API) is not set, returns avg:null silently.
 * Expected API: GET <base>?s=<scryfallId> -> { ok:boolean; avg:number|null }
 */
export function useSpellgatherAvg(scryfallId?: string | null) {
  const base =
    getEnv("VITE_SG_AVG_API") ||
    getEnv("NEXT_PUBLIC_SG_AVG_API") ||
    ""; // unset => feature off

  const sid = (scryfallId || "").trim();
  const shouldFetch = Boolean(base && sid);

  const [data, setData] = useState<{ ok: boolean; avg: number | null } | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!shouldFetch) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }

    const url = buildUrl(base, { s: sid });
    const now = Date.now();
    const cached = MEMO[url];
    if (cached && now - cached.t < TTL) {
      setData(cached.data);
      return;
    }

    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true);
    setError(null);

    fetch(url, {
      signal: ac.signal,
      // never cache at the browser layer; we do our own TTL memo
      cache: "no-store",
      headers: { Accept: "application/json" },
      credentials: "omit",
      mode: "cors",
    })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((j: { ok: boolean; avg: number | null }) => {
        MEMO[url] = { t: Date.now(), data: j };
        setData(j);
      })
      .catch((e: any) => {
        if (e?.name !== "AbortError") setError(e);
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });

    return () => ac.abort();
  }, [shouldFetch, base, sid]);

  return {
    avg: data?.avg ?? null,
    isLoading: shouldFetch && loading,
    error: shouldFetch ? error : null,
  };
}
